import { FabricObject, util as fabricNativeUtils, Point as FabricPoint } from 'fabric';
import { Polygon } from 'polygon-clipping';
import React from 'react';

import { LineEdge } from 'editor/src/store/design/types';

import CustomFabricCircle from 'editor/src/fabric/CustomFabricCircle';
import CustomFabricLine from 'editor/src/fabric/CustomFabricLine';
import CustomFabricObject from 'editor/src/fabric/CustomFabricObject';
import CustomFabricRect from 'editor/src/fabric/CustomFabricRect';

import { setClipPath } from 'editor/src/component/EditorArea/fabricComponents/fabricUtils';
import getClipPath from 'editor/src/component/EditorArea/Spread/Page/MediaElement/getClipPath';

import getLineRectFromRef from './getLineRectFromRef';
import { getArrowBoundingRectFromRef, getEdgeOffset, getLineCapOffset } from './utils';

const onStartPointMove = (
  rectRef: React.RefObject<CustomFabricRect>,
  lineRef: React.RefObject<CustomFabricLine>,
  startPointRef: React.RefObject<CustomFabricCircle>,
  endPointRef: React.RefObject<CustomFabricCircle>,
  edge1Ref: React.RefObject<CustomFabricObject>,
  edge2Ref: React.RefObject<CustomFabricObject>,
  edge1: LineEdge,
  edge2: LineEdge,
  contentClipPolygons: Polygon[],
  contentClipPath: FabricObject | undefined,
) => {
  if (!rectRef.current) {
    return;
  }
  const { left = 0, top = 0, width = 0, height = 0, angle = 0 } = rectRef.current;

  const rounded = lineRef.current?.strokeLineCap === 'round';
  const lineCapOffset = getLineCapOffset(rounded, height, angle);

  const strokeWidthOffset = fabricNativeUtils.rotatePoint(
    new FabricPoint(height / 2, 0),
    new FabricPoint(0, 0),
    fabricNativeUtils.degreesToRadians(angle + 90),
  );

  const endPoint = fabricNativeUtils.rotatePoint(
    new FabricPoint(left + width, top),
    new FabricPoint(left, top),
    fabricNativeUtils.degreesToRadians(angle),
  );

  const edge1Offset = getEdgeOffset(edge1, height, angle);
  const edge2Offset = getEdgeOffset(edge2, height, angle);
  startPointRef.current?.set({
    left: left + strokeWidthOffset.x,
    top: top + strokeWidthOffset.y,
  });
  endPointRef.current?.set({
    left: endPoint.x + strokeWidthOffset.x,
    top: endPoint.y + strokeWidthOffset.y,
  });
  lineRef.current?.set({
    x1: left + strokeWidthOffset.x + lineCapOffset.x,
    y1: top + strokeWidthOffset.y + lineCapOffset.y,
    x2: endPoint.x + strokeWidthOffset.x - lineCapOffset.x,
    y2: endPoint.y + strokeWidthOffset.y - lineCapOffset.y,
  });

  const lineBBox = getLineRectFromRef(lineRef, height);
  const clipPath = getClipPath(lineBBox, contentClipPolygons, false, contentClipPath);
  setClipPath(lineRef.current, clipPath);

  edge1Ref.current?.set({
    left: left + strokeWidthOffset.x,
    top: top + strokeWidthOffset.y,
  });
  edge2Ref.current?.set({
    left: endPoint.x + strokeWidthOffset.x,
    top: endPoint.y + strokeWidthOffset.y,
  });

  setClipPath(
    edge1Ref.current,
    getClipPath(getArrowBoundingRectFromRef(edge1Ref, height, angle), contentClipPolygons, false, contentClipPath),
  );
  setClipPath(
    edge2Ref.current,
    getClipPath(
      getArrowBoundingRectFromRef(edge2Ref, height, -180 + angle),
      contentClipPolygons,
      false,
      contentClipPath,
    ),
  );

  // need to remove the edge offset from each side
  if (lineRef.current) {
    const { x1 = 0, y1 = 0, x2 = 0, y2 = 0 } = lineRef.current;
    lineRef.current?.set({
      x1: x1 + edge1Offset.x,
      y1: y1 + edge1Offset.y,
      x2: x2 - edge2Offset.x,
      y2: y2 - edge2Offset.y,
    });
  }
};

export default onStartPointMove;
